import type { NamedProfile, OwnIdentity, OwnProfile, Profile } from "@beacon/core/access"
import type { PhotoUpload } from "@beacon/core/photos"
import { queryOptions } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"
import { Refused } from "@/administration/refused.ts"
import { apiBase } from "@/sign-in/client.ts"
import { isHeic } from "./describe.ts"

/** Your own Profile, as you edit it. */
export const ownProfileQuery = queryOptions({
  queryKey: ["me", "profile"],
  queryFn: () => call<OwnProfile>("GET", "/me/profile"),
})

/** Your own Identities: every email, account and number Beacon knows you by. */
export const ownIdentitiesQuery = queryOptions({
  queryKey: ["me", "identities"],
  queryFn: () => call<{ identities: OwnIdentity[] }>("GET", "/me/identities"),
  select: ({ identities }) => identities,
})

/** Adds an email to you, unproven, and emails a code to it; again, it sends a new code. */
export const addEmail = (address: string) =>
  call<OwnIdentity>("POST", "/me/identities/emails", { address })

/**
 * Sends the browser off to sign in to GitHub, proving which account is yours. It is a navigation,
 * not a call: GitHub brings the browser back to the web API, which lands it on your Profile saying
 * how it went.
 */
export const connectGitHub = () => window.location.assign(`${apiBase}/me/identities/github`)

/** Proves an email you added with the code sent to it. */
export const proveEmail = (identityId: string, code: string) =>
  call<OwnIdentity>("POST", `/me/identities/${identityId}/proof`, { code })

/** Adds a mobile number to you, in any way people write one with its country code. */
export const addMobile = (number: string) =>
  call<OwnIdentity>("POST", "/me/identities/mobiles", { number })

/** Chooses your username, or changes it. */
export const chooseUsername = (username: string) =>
  call<OwnIdentity>("PUT", "/me/username", { username })

/** Whether you have a password to sign in with. */
export const passwordQuery = queryOptions({
  queryKey: ["me", "password"],
  queryFn: () => call<{ set: boolean }>("GET", "/me/password"),
  select: ({ set }) => set,
})

/** Sets your password, or changes it, which takes the one you have now as `current`. */
export const setPassword = (change: { current?: string; password: string }) =>
  call<void>("PUT", "/me/password", change)

/** Makes one of your verified emails the one Beacon writes to. */
export const choosePrimaryEmail = (identityId: string) =>
  call<void>("PUT", "/me/primary-email", { identityId })

/** Removes an Identity you added yourself. */
export const removeIdentity = (identityId: string) =>
  call<void>("DELETE", `/me/identities/${identityId}`)

/** Every Member's photo by their id, as the URL its sizes are under, for every avatar drawn. */
export const photosQuery = queryOptions({
  queryKey: ["people", "photos"],
  queryFn: () => call<{ photos: Record<string, string> }>("GET", "/people/photos"),
  select: ({ photos }) => photos,
  staleTime: 5 * 60_000,
})

/** Saves the fields an edit changes, answering the Profile it leaves. */
export const editProfile = (edit: Partial<Profile>) =>
  call<NamedProfile>("PUT", "/me/profile", edit)

/** Marks your seeded Profile checked, so the first-sign-in check never shows again. */
export const markProfileChecked = () => call<void>("POST", "/me/profile/checked")

/** Removes your own photo. */
export const removeOwnPhoto = () => call<void>("DELETE", "/me/photo")

/**
 * A file as the server can read it: an iPhone's HEIC turned into JPEG here in the browser, since
 * the server reads only JPEG, PNG and WebP, and anything else as it is. The converter is fetched
 * only when a HEIC photo needs it.
 */
async function readable(file: File): Promise<Blob> {
  if (!isHeic(file)) return file
  const { heicTo } = await import("heic-to/csp")
  return heicTo({ blob: file, type: "image/jpeg", quality: 0.9 })
}

/**
 * Makes a file your photo: converted if it is HEIC, put straight into the store through the URL
 * the web API signs, then confirmed, which is when the server reads it. Throws `Refused` with the
 * web API's refusal (`too-large`, `unreadable`, `invalid-photo`), or `upload-failed` when the store
 * refused the bytes.
 */
export async function uploadOwnPhoto(file: File) {
  const photo = await readable(file).catch(() => {
    throw new Refused("unreadable", {})
  })
  const upload = await call<PhotoUpload>("POST", "/me/photo/uploads", {
    contentType: photo.type,
    size: photo.size,
  })
  const put = await fetch(upload.url, { method: "PUT", headers: upload.headers, body: photo })
  if (!put.ok) throw new Refused("upload-failed", {})
  return call<{ version: string; url: string }>(
    "POST",
    `/me/photo/uploads/${upload.uploadId}/confirm`,
  )
}
